'use client';

import { useState } from 'react';
import Link from 'next/link';
import {
  Nav, NavItem, NavLink, TabContent, TabPane,
} from 'reactstrap';
import classnames from 'classnames';

import CopyButton from '../components/Shared/CopyButton';
import { useAppSelector } from '../../lib/hooks';
import { selectIsLoggedIn } from '../../lib/features/auth/authSlice';

const KEYS_PAGE = '/profile/api';

// Signed-out visitors go through login and land on the keys tab afterwards.
export function KeyCta({ className = 'btn btn-primary' }) {
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const href = isLoggedIn ? KEYS_PAGE : `/auth-login?from=${encodeURIComponent(KEYS_PAGE)}`;
  return (
    <Link href={href} className={className}>
      <i className="uil uil-key-skeleton me-1" />
      {isLoggedIn ? 'Create an API key' : 'Log in to get an API key'}
    </Link>
  );
}

const KEY_PLACEHOLDER = 'svh_YOUR_KEY';

const buildSnippets = (url) => [
  {
    id: 'claude-code',
    title: 'Claude Code',
    file: 'Terminal',
    note: 'Adds the server for the current project. Add --scope user to make it available in every project.',
    code: `claude mcp add --transport http svghmi ${url} --header "Authorization: Bearer ${KEY_PLACEHOLDER}"`,
  },
  {
    id: 'claude-code-oauth',
    title: 'Claude Code (sign in)',
    file: 'Terminal',
    note: 'No key: Claude Code opens the browser (or run /mcp in Claude Code to authenticate), you sign in to svghmi.pro and approve access. The page will say it returns you to an app on this computer — approve only if you just started it.',
    oauth: true,
    code: `claude mcp add --transport http svghmi ${url}`,
  },
  {
    id: 'codex',
    title: 'Codex',
    file: 'Terminal',
    note: 'OpenAI Codex CLI. The login command opens the browser: sign in to svghmi.pro and approve access (the page will say it returns you to an app on this computer). To use an API key instead, set SVGHMI_API_KEY and add --bearer-token-env-var SVGHMI_API_KEY to the first command, skipping the login.',
    oauth: true,
    code: `codex mcp add svghmi --url ${url}\ncodex mcp login svghmi`,
  },
  {
    id: 'cursor',
    title: 'Cursor',
    file: '.cursor/mcp.json',
    note: 'Project-level config. For all projects use ~/.cursor/mcp.json instead.',
    code: JSON.stringify({
      mcpServers: {
        svghmi: {
          url,
          headers: { Authorization: `Bearer ${KEY_PLACEHOLDER}` },
        },
      },
    }, null, 2),
  },
  {
    id: 'vscode',
    title: 'VS Code',
    file: '.vscode/mcp.json',
    note: 'Works with GitHub Copilot agent mode. Start the server from the MCP view or the file itself.',
    code: JSON.stringify({
      servers: {
        svghmi: {
          type: 'http',
          url,
          headers: { Authorization: `Bearer ${KEY_PLACEHOLDER}` },
        },
      },
    }, null, 2),
  },
  {
    id: 'claude-desktop',
    title: 'Claude Desktop',
    file: 'claude_desktop_config.json',
    note: 'Uses the mcp-remote bridge (needs Node.js). The header is passed through an environment variable so the space in it survives on Windows. Restart Claude Desktop after editing.',
    code: JSON.stringify({
      mcpServers: {
        svghmi: {
          command: 'npx',
          // Literal ${...}: mcp-remote expands it from `env`, not JavaScript.
          // eslint-disable-next-line no-template-curly-in-string
          args: ['-y', 'mcp-remote', url, '--header', 'Authorization:${SVGHMI_AUTH}'],
          env: { SVGHMI_AUTH: `Bearer ${KEY_PLACEHOLDER}` },
        },
      },
    }, null, 2),
  },
  {
    id: 'generic',
    title: 'Other clients',
    file: 'Any MCP client with Streamable HTTP',
    note: 'Transport: Streamable HTTP. Authentication: a Bearer token in the Authorization header.',
    code: `URL:    ${url}\nHeader: Authorization: Bearer ${KEY_PLACEHOLDER}`,
  },
];

export function SetupSnippets({ url }) {
  const snippets = buildSnippets(url);
  const [active, setActive] = useState(snippets[0].id);

  return (
    <>
      <Nav pills className="nav-justified flex-column flex-sm-row rounded shadow p-2 mb-0">
        {snippets.map((s) => (
          <NavItem key={s.id} className="m-1">
            <NavLink
              href="#"
              className={classnames({ active: active === s.id }, 'rounded')}
              onClick={(e) => {
                e.preventDefault();
                setActive(s.id);
              }}
            >
              <h6 className="mb-0">{s.title}</h6>
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <TabContent activeTab={active} className="mt-3">
        {snippets.map((s) => (
          <TabPane key={s.id} tabId={s.id} className="bg-white shadow rounded p-4">
            <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
              <span className="text-muted small font-monospace text-break">{s.file}</span>
              <CopyButton text={s.code} />
            </div>
            <pre className="bg-light border rounded p-3 mb-2 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              <code>{s.code}</code>
            </pre>
            <p className="text-muted small mb-0">
              {s.note}
              {s.oauth ? null : (
                <>
                  {' '}
                  Replace
                  {' '}
                  <code>{KEY_PLACEHOLDER}</code>
                  {' '}
                  with your key.
                </>
              )}
            </p>
          </TabPane>
        ))}
      </TabContent>
    </>
  );
}
